import { useMemo, useState } from 'react';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Empty, EmptyDescription, EmptyTitle } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import { CountryInput } from '@/views/Events/EventOrder/PublicEventOrderForm/components/CountryInput';
import {
  CarrierEntry,
  DefaultCarrierConfig,
} from '@/components/carrier/DefaultCarrierConfig';
import {
  extractCarrierErrorMessage,
  withoutUnsupportedServices,
} from '@/components/carrier/carrierErrors';
import {
  GetDefaultCarrierByCountryConfigQuery,
  useGetDefaultCarrierByCountryConfigQuery,
} from '@/graphql/queries/getDefaultCarrierByCountryConfig.generated';
import { useSetDefaultCarrierByCountryConfigMutation } from '@/graphql/mutations/setDefaultCarrierByCountryConfig.generated';
import i18n from '@/utils/i18n';

type CountryEntry = {
  country_code: string;
  defaults: CarrierEntry[];
};

function getCountryDisplayName(code: string): string {
  try {
    return (
      new Intl.DisplayNames([i18n.language || 'en'], { type: 'region' }).of(
        code,
      ) || code
    );
  } catch {
    return code;
  }
}

function entriesEqual(a: CountryEntry[], b: CountryEntry[]): boolean {
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));
}

function normalize(entries: CountryEntry[]): CountryEntry[] {
  return [...entries]
    .sort((x, y) => x.country_code.localeCompare(y.country_code))
    .map((c) => ({
      country_code: c.country_code,
      defaults: [...c.defaults].sort((x, y) =>
        x.carrier_service.localeCompare(y.carrier_service),
      ),
    }));
}

function fromQuery(
  data: GetDefaultCarrierByCountryConfigQuery,
): CountryEntry[] {
  return data.getDefaultCarrierByCountryConfig.map((e) => ({
    country_code: e.country_code,
    defaults: withoutUnsupportedServices(
      e.defaults.map((d) => ({
        carrier_service: d.carrier_service,
        carrier: d.carrier,
      })),
    ),
  }));
}

export default function CarrierCountryConfigPage() {
  const { data, loading, refetch } = useGetDefaultCarrierByCountryConfigQuery();

  if (loading && !data) {
    return (
      <div className="tw:flex tw:justify-center tw:py-8">
        <Spinner className="tw:size-6" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <CarrierCountryConfigEditor
      key={JSON.stringify(data.getDefaultCarrierByCountryConfig)}
      initial={fromQuery(data)}
      onSaved={() => refetch()}
    />
  );
}

type EditorProps = {
  initial: CountryEntry[];
  onSaved: () => void;
};

function CarrierCountryConfigEditor({ initial, onSaved }: EditorProps) {
  const [saveMutation, { loading: saving }] =
    useSetDefaultCarrierByCountryConfigMutation();

  const [entries, setEntries] = useState<CountryEntry[]>(initial);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newCountry, setNewCountry] = useState<string | undefined>(undefined);

  const isDirty = useMemo(
    () => !entriesEqual(entries, initial),
    [entries, initial],
  );

  const updateCountry = (countryCode: string, defaults: CarrierEntry[]) => {
    setEntries((prev) =>
      prev.map((c) =>
        c.country_code === countryCode ? { ...c, defaults } : c,
      ),
    );
  };

  const removeCountry = (countryCode: string) => {
    const name = getCountryDisplayName(countryCode);
    if (
      !window.confirm(i18n.t('Carrier:removeCountryConfirm', { country: name }))
    )
      return;
    setEntries((prev) => prev.filter((c) => c.country_code !== countryCode));
  };

  const handleAddCountry = () => {
    if (!newCountry) return;
    if (entries.some((c) => c.country_code === newCountry)) {
      setAddDialogOpen(false);
      setNewCountry(undefined);
      return;
    }
    setEntries((prev) => [...prev, { country_code: newCountry, defaults: [] }]);
    setAddDialogOpen(false);
    setNewCountry(undefined);
  };

  const handleSave = async () => {
    try {
      await saveMutation({
        variables: {
          entries: entries.map((c) => ({
            country_code: c.country_code,
            defaults: c.defaults.map((d) => ({
              carrier_service: d.carrier_service,
              carrier: d.carrier,
            })),
          })),
        },
      });
      toast.success(i18n.t('Carrier:saveSuccess'));
      onSaved();
    } catch (err) {
      toast.error(extractCarrierErrorMessage(err));
    }
  };

  return (
    <div className="tw:p-6 tw:flex tw:flex-col tw:gap-4 tw:max-w-4xl">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-4">
        <div>
          <h2 className="tw:text-xl tw:font-medium">
            {i18n.t('Carrier:countryConfigTitle')}
          </h2>
          <p className="tw:text-muted-foreground tw:text-sm tw:mt-1">
            {i18n.t('Carrier:countryConfigIntro')}
          </p>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2 tw:shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => setAddDialogOpen(true)}
            disabled={saving}
          >
            <PlusIcon /> {i18n.t('Carrier:addCountry')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saving}
          >
            {saving ? <Spinner /> : null}
            {saving ? i18n.t('Common:saving') : i18n.t('Common:save')}
          </Button>
        </div>
      </div>

      {isDirty && (
        <p className="tw:text-amber-600 tw:text-sm">
          {i18n.t('Carrier:unsavedChanges')}
        </p>
      )}

      {entries.length === 0 && (
        <Empty>
          <EmptyTitle>{i18n.t('Carrier:noCountriesConfigured')}</EmptyTitle>
          <EmptyDescription>
            {i18n.t('Carrier:countryConfigIntro')}
          </EmptyDescription>
        </Empty>
      )}

      {entries.map((entry) => (
        <Card key={entry.country_code}>
          <CardHeader>
            <CardTitle>{getCountryDisplayName(entry.country_code)}</CardTitle>
            <CardDescription>{entry.country_code}</CardDescription>
            <div className="tw:col-start-2 tw:row-span-2 tw:row-start-1 tw:self-start tw:justify-self-end">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeCountry(entry.country_code)}
                aria-label={i18n.t('Carrier:removeCountry')}
              >
                <TrashIcon />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <DefaultCarrierConfig
              value={entry.defaults}
              onChange={(next) => updateCountry(entry.country_code, next)}
            />
          </CardContent>
        </Card>
      ))}

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{i18n.t('Carrier:addCountryDialogTitle')}</DialogTitle>
            <DialogDescription>
              {i18n.t('Carrier:addCountryDialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <CountryInput
            value={newCountry}
            onChange={setNewCountry}
            placeholder={i18n.t('Carrier:selectCountry')}
            triggerClassName="tw:w-full"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAddDialogOpen(false);
                setNewCountry(undefined);
              }}
            >
              {i18n.t('Common:cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleAddCountry}
              disabled={!newCountry}
            >
              {i18n.t('Common:add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
